/**
 * Design System / Theme Configuration
 *
 * Centralized design tokens for the entire application.
 * Modify values here to update styles across all components.
 */

// ===================
// COLORS
// ===================
// Light palette is the default; dark overrides every color that changes.
const lightColors = {
  // Accent
  accent: '#22c55e',

  // Backgrounds
  background: '#ffffff',
  backgroundCard: '#f8fafc',

  // Text
  textPrimary: '#2c2c2c',
  textSecondary: '#666',
  textMuted: '#888',
  textLight: '#444',

  // Borders / chrome
  border: '#eaecf0',
  headerBackground: 'rgba(255,255,255,0.85)'
};

const darkColors = {
  background: '#0e0f11',
  backgroundCard: '#17191c',

  textPrimary: '#ededed',
  textSecondary: '#a1a1aa',
  textMuted: '#71717a',
  textLight: '#d4d4d8',

  border: '#26282c',
  headerBackground: 'rgba(14,15,17,0.8)'
};

const toCssVars = (colors) =>
  Object.entries(colors)
    .map(([key, value]) => `--c-${key}: ${value};`)
    .join('\n  ');

// Injected once at the app root; <html data-theme="dark"> switches palettes
export const colorModeCss = `
:root {
  color-scheme: light;
  ${toCssVars(lightColors)}
}
:root[data-theme='dark'] {
  color-scheme: dark;
  ${toCssVars(darkColors)}
}
`;

const theme = {
  // Every color resolves to a CSS variable, so components follow the active mode
  colors: Object.fromEntries(
    Object.keys(lightColors).map((key) => [key, `var(--c-${key})`])
  ),

  // ===================
  // TYPOGRAPHY
  // ===================
  typography: {
    // Font families
    fontFamily:
      "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono:
      "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",

    // Font sizes
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.85rem', // ~13.6px
      base: '0.9rem', // ~14.4px
      md: '1rem', // 16px
      lg: '1.1rem', // ~17.6px
      xl: '1.3rem', // ~20.8px
      '2xl': '1.5rem', // 24px
      '3xl': '2rem', // 32px
      '4xl': '2.5rem' // 40px
    },

    // Font weights
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },

    // Line heights
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.6
    },

    // Letter spacing
    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.05em'
    }
  },

  // ===================
  // BORDER RADIUS
  // ===================
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px'
  },

  // ===================
  // TRANSITIONS
  // ===================
  transitions: {
    fast: '0.2s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
    smooth: '0.6s cubic-bezier(0.4, 0, 0.2, 1)'
  },

  // ===================
  // LAYOUT
  // ===================
  layout: {
    contentWidth: '880px'
  },

  // ===================
  // Z-INDEX
  // ===================
  zIndex: {
    base: 0,
    dropdown: 100,
    sticky: 200,
    modal: 300,
    tooltip: 400
  }
};

export default theme;
